import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FieldArray } from 'redux-form';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import PrincipalRoles from './PrincipalRoles';
import FormHeader from '../../../../../portal/app/components/FormHeader';
import appSubscriptionsService from '../../../services/appSubscriptions/appSubscriptionsService';
import organisationUserGroupsService from '../../../services/userGroups/organisationUserGroupsService';

const PrincipalRolesTab = ({
  appSubscriptionsFetch,
  dispatchAppSubscriptionsGet,
  dispatchOrganisationUserGroupsGet,
  organisationUserGroupsFetch,
  organisationId,
  editMode,
  readOnly,
  formName,
  hideUserGroups,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const id = organisationId || params.id;

  useEffect(() => dispatchAppSubscriptionsGet(id, t), [t, id, dispatchAppSubscriptionsGet]);
  useEffect(() => dispatchOrganisationUserGroupsGet(id), [id, dispatchOrganisationUserGroupsGet]);

  const apps = useMemo(
    () =>
      (appSubscriptionsFetch.value || []).map(app => ({
        ...app,
        key: `${app.appInstallation.tag}_${app.community.tag}`,
        value: `${app.appInstallation.tag}_${app.community.tag}`,
        text: `${app.alias || app.appInstallation?.name} (${app.community?.name})`,
      })),
    [appSubscriptionsFetch.value]
  );

  const userGroupOptions = useMemo(
    () =>
      (organisationUserGroupsFetch.value || []).map(oug => ({
        key: oug.id,
        value: oug.id,
        text: oug.name,
      })),
    [organisationUserGroupsFetch.value]
  );

  if (appSubscriptionsFetch.pending) return null;

  return (
    <>
      {userGroupOptions?.length > 0 && !editMode && !hideUserGroups && (
        <>
          <FormHeader content={t('userGroups')} />
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <Field
              fluid
              multiple
              clearable
              placeholder={t('selectUserGroups')}
              name="userGroups"
              options={userGroupOptions}
              valueComponent={SelectFieldValueComponent}
              disabled={false}
            />
          </Grid.Column>
          <FormHeader content={t('roles')} />
        </>
      )}
      <FieldArray
        name="communityApps"
        component={PrincipalRoles}
        editMode={editMode}
        apps={apps}
        readOnly={readOnly}
        formName={formName}
      />
    </>
  );
};

PrincipalRolesTab.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
  appSubscriptionsFetch: PropTypes.object.isRequired,
  dispatchAppSubscriptionsGet: PropTypes.func.isRequired,
  dispatchOrganisationUserGroupsGet: PropTypes.func.isRequired,
  organisationUserGroupsFetch: PropTypes.object.isRequired,
  organisationId: PropTypes.string,
  formName: PropTypes.string,
  hideUserGroups: PropTypes.bool,
};

PrincipalRolesTab.defaultProps = {
  hideUserGroups: false,
  organisationId: undefined,
  formName: undefined,
};

export default compose(organisationUserGroupsService, appSubscriptionsService)(PrincipalRolesTab);
